import React, { useState, useRef, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useClickOutside, useDelayUnmount } from 'hooks';
import { ArrowBottomIcon } from 'assets/icons';
import styles from './styles.module.scss';
import useTranslation from 'hooks/useTranslation';
import { HTTPS_REGEX } from 'utils/regex';

const ANIM_DURATION = 250;

//TODO: add debouncing for searchbox
const SelectBox = ({
  title,
  options,
  disabled,
  loadingOptions,
  onChange,
  className,
  value,
  hasError,
  searchboxPlaceholder,
  enableSearchBox,
  searchKey,
  renderCustomHeadContent,
  returnForm,
  classNames,
  // specially for responsive robot
  isResponsiveRobot,
  nestedValueChange,
  nestedValueId,
}) => {
  const [searchBoxInput, setSearchBoxInput] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredItemIndex, setHoveredItemIndex] = useState(0);
  const [appearMenu, setAppearMenu] = useState(false);
  const shouldRender = useDelayUnmount(isMenuOpen, ANIM_DURATION);
  const dropdownMenuRef = useRef(null);
  const menuItemsRef = useRef([]);
  const searchInputRef = useRef(null);

  const { getTranslation } = useTranslation('COMMON');
  const { t } = getTranslation();

  const renderedTitle = title || t('SELECT_BOX_TITLE');
  const selectedOption = value && options.find((item) => item.value === value);

  const handleClickOutsideDropdownMenu = () => {
    if (isMenuOpen) setIsMenuOpen(false);
  };

  useClickOutside(dropdownMenuRef, handleClickOutsideDropdownMenu);

  const handleClickDropdownHead = (e) => {
    e.stopPropagation();
    if (!disabled) setIsMenuOpen(!isMenuOpen);
  };

  const handleActionForArrowButtonClick = (newIndex) => {
    const elementHeight = menuItemsRef.current?.[newIndex]?.offsetHeight;
    const newElementDistanceFromTop =
      menuItemsRef.current?.[newIndex]?.offsetTop;
    const menuHeight = dropdownMenuRef.current?.clientHeight;
    const scrollDistanceFromTop = dropdownMenuRef.current?.scrollTop;

    // moving down and element is not visible
    if (newElementDistanceFromTop - (menuHeight + scrollDistanceFromTop) >= 0) {
      dropdownMenuRef.current.scrollTop =
        dropdownMenuRef.current?.scrollTop + elementHeight;
    }

    // moving up and element is not visible
    if (newElementDistanceFromTop < scrollDistanceFromTop) {
      dropdownMenuRef.current.scrollTop =
        dropdownMenuRef.current?.scrollTop - elementHeight;
    }

    return newIndex;
  };

  const handleMouseEnterItem = (index) => {
    setHoveredItemIndex(index);
  };

  const handleClickOptionItem = (option) => {
    const value = returnForm === 'option' ? option : option.value;

    if (isResponsiveRobot) {
      nestedValueChange(nestedValueId, value);
      setIsMenuOpen(false);
      return;
    }

    onChange(value);
    setIsMenuOpen(false);
  };

  const renderSkeleton = (
    <div className={styles.item}>
      <div className={`${styles.skeleton_title} ${styles.skeleton}`}></div>
      <div className={`${styles.skeleton_subscript} ${styles.skeleton}`}></div>
    </div>
  );

  const getFilteredOptions = () => {
    if (searchBoxInput === '') {
      return options;
    }

    return options.filter((item) =>
      item[searchKey]?.toLowerCase().includes(searchBoxInput.toLowerCase())
    );
  };

  const filteredOptions = getFilteredOptions();

  const renderOptions = filteredOptions.map((item, index) => (
    <li
      onClick={() => handleClickOptionItem(item)}
      key={typeof item.value === 'object' ? index : String(item.value)}
      disabled={item.disabled}
      className={`${styles.item} ${
        item.value === selectedOption?.value
          ? styles.selected
          : index === hoveredItemIndex
          ? styles.hovered
          : ''
      }`}
      onMouseEnter={() => handleMouseEnterItem(index)}
      ref={(el) => (menuItemsRef.current[index] = el)}
    >
      <div
        className={styles.title}
        title={typeof item.title === 'string' && item.title}
      >
        {item.title}
      </div>
      {item.subTitle && (
        <div className={styles.subscript} title={item.url}>
          {HTTPS_REGEX.test(item.subTitle)
            ? item.subTitle.replace(HTTPS_REGEX, '')
            : item.subTitle}
        </div>
      )}
    </li>
  ));

  const renderMenuContent = () => {
    // loading
    if (loadingOptions) {
      return (
        <>
          {renderSkeleton}
          {renderSkeleton}
          {renderSkeleton}
        </>
      );
    }

    // empty list
    if (options.length === 0) {
      return (
        <div className={styles.empty_list_message}>{t('SELECT_BOX_EMPTY')}</div>
      );
    }

    return renderOptions;
  };

  const handleActionForEnterButton = () => {
    const selectedOption = options.find(
      (item, index) => index === hoveredItemIndex
    );
    handleClickOptionItem(selectedOption);
  };

  const handleArrowButtonClick = (e) => {
    // do not proceed if searchbox field is focused
    if (document.activeElement === searchInputRef.current) return;

    e.preventDefault();
    const keycode = e.keyCode;

    // enter button click
    if (keycode === 13) {
      handleActionForEnterButton();
      return;
    }

    //38 => keycode for up and 40 for down arrow
    const newIndex =
      keycode === 38
        ? hoveredItemIndex - 1
        : keycode === 40
        ? hoveredItemIndex + 1
        : hoveredItemIndex;

    if (newIndex < 0 || newIndex >= options?.length) return;

    setHoveredItemIndex(newIndex);
    handleActionForArrowButtonClick(newIndex);
  };

  const handleChangeSearchBoxInput = (e) => {
    setSearchBoxInput(e.target.value);
  };

  const renderDropdownHeadContent = () => {
    if (!selectedOption) return renderedTitle;

    if (renderCustomHeadContent) return renderCustomHeadContent(selectedOption);

    return selectedOption.title;
  };

  useEffect(() => {
    isMenuOpen && document.addEventListener('keydown', handleArrowButtonClick);

    return () =>
      document.removeEventListener('keydown', handleArrowButtonClick);
  });

  // mount animation
  useEffect(() => {
    if (isMenuOpen) {
      setTimeout(() => setAppearMenu(true), 50);
    } else setAppearMenu(false);
  }, [isMenuOpen]);

  return (
    <div
      className={`${styles.dropdown_container} ${
        disabled ? styles.disabled : ''
      } ${className} ${hasError ? styles.has_error : ''}`}
    >
      {/* head */}
      <div
        onClick={handleClickDropdownHead}
        className={`${styles.dropdown_head} ${classNames['head'] || ''} `}
      >
        <div className={styles.title}>{renderDropdownHeadContent()}</div>
        <img
          className={`${styles.dropdown_head_chevron} ${
            isMenuOpen ? styles.rotate : ''
          }`}
          src={ArrowBottomIcon}
          alt=''
        />
      </div>

      <>
        {/* list */}
        {shouldRender && (
          <div
            className={`${styles.dropdown_menu} ${
              !isMenuOpen ? styles.disappear : ''
            } ${appearMenu ? styles.appear : ''} ${classNames['list'] || ''}`}
            ref={dropdownMenuRef}
          >
            {/* search-box */}
            {enableSearchBox && (
              <div
                className={`${styles.search_box} ${
                  !isMenuOpen ? styles.disappear : ''
                } ${appearMenu ? styles.appear : ''} `}
              >
                <input
                  name='searchbox'
                  type='text'
                  onChange={handleChangeSearchBoxInput}
                  placeholder={searchboxPlaceholder}
                  value={searchBoxInput}
                  ref={searchInputRef}
                  autoFocus={true}
                  className={styles.search_box__input}
                />
              </div>
            )}

            {/* results list */}
            <ul>{renderMenuContent()}</ul>
          </div>
        )}
      </>
    </div>
  );
};

// TODO: update props
SelectBox.defaultProps = {
  handleClickOptionItem: () => {},
  disabled: false,
  searchboxPlaceholder: 'Search',
  enableSearchBox: false,
  classNames: {},
};

const OPTION_SHAPE = PropTypes.shape({
  title: PropTypes.string,
  value: PropTypes.string,
});

SelectBox.propTypes = {
  title: PropTypes.string,
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(OPTION_SHAPE),
    PropTypes.arrayOf(PropTypes.string),
  ]),
  disabled: PropTypes.bool,
  handleClickOptionItem: PropTypes.func,
  className: PropTypes.string,
  searchKey: PropTypes.string,
};

export default SelectBox;
